import { useEffect, useState } from "react";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import type { IgnoreRule, ScreeningDocument } from "@/services/api/guardiansv2/types";
import {
  canIgnoreAlert,
  programmaticSignalFailures,
  signalTypeFromCheck,
} from "./canIgnoreAlert";

export interface IgnoreAlertModalProps {
  alertId: string;
  open: boolean;
  screening?: ScreeningDocument | null;
  signalType?: string | null;
  onClose: () => void;
  onIgnored: () => void;
}

function previewRule(
  signalType: string,
  label: string,
  catalogueKey: string | null | undefined
): IgnoreRule {
  const catalogue = catalogueKey ?? "this task type";
  return {
    id: "(new)",
    kind: "ignore",
    enabled: true,
    signal_type: signalType,
    label: `Ignore: ${label}`,
    text: `Ignore programmatic issues matching ${signalType} on ${catalogue}.`,
  };
}

export function IgnoreAlertModal({
  alertId,
  open,
  screening: screeningProp,
  signalType,
  onClose,
  onIgnored,
}: IgnoreAlertModalProps) {
  const [screening, setScreening] = useState<ScreeningDocument | null>(screeningProp ?? null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (screeningProp) {
      setScreening(screeningProp);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void GuardiansV2API.getScreeningByAlert(alertId)
      .then((doc) => {
        if (!cancelled) setScreening(doc);
      })
      .catch(() => {
        if (!cancelled) setScreening(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [alertId, open, screeningProp]);

  if (!open) return null;

  const failures = programmaticSignalFailures(screening);
  const chosen =
    failures.find((row) => signalTypeFromCheck(row) === signalType) ?? failures[0] ?? null;
  const resolvedType = chosen ? signalTypeFromCheck(chosen) : null;
  const preview = chosen && resolvedType
    ? previewRule(resolvedType, chosen.title, screening?.catalogue_key)
    : null;

  const handleConfirm = async () => {
    if (!resolvedType) return;
    setSubmitting(true);
    setError(null);
    try {
      await GuardiansV2API.ignoreAlert(alertId, { signal_type: resolvedType });
      onIgnored();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add ignore rule");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={onClose}
      data-testid="ignore-alert-modal"
    >
      <div
        className="w-full max-w-lg rounded-xl border border-slate-600 bg-slate-800 p-4 shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h3 className="text-sm font-semibold text-slate-100">Add ignore rule</h3>
        {loading ? (
          <p className="mt-3 text-sm text-slate-400">Loading alert details…</p>
        ) : !canIgnoreAlert(screening) ? (
          <p className="mt-3 text-sm text-slate-400">
            This alert has no programmatic issues to ignore. Disable a failing LLM rule on the Rules tab instead.
          </p>
        ) : preview ? (
          <div className="mt-3 space-y-3 text-sm text-slate-300">
            <p>
              This adds an ignore rule to{" "}
              <span className="font-medium text-slate-100">{screening?.catalogue_key ?? "the task catalogue"}</span>{" "}
              and archives the alert. Future screenings will suppress this programmatic issue type.
            </p>
            <div className="rounded-lg border border-white/10 bg-slate-900/50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rule preview</p>
              <p className="mt-1 font-medium text-slate-100">{preview.label}</p>
              <p className="mt-1 text-slate-400">{preview.text}</p>
              <p className="mt-2 font-mono text-xs text-slate-500">signal_type: {preview.signal_type}</p>
            </div>
          </div>
        ) : null}
        {error ? <p className="mt-3 text-sm text-rose-300">{error}</p> : null}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting || !preview || !canIgnoreAlert(screening)}
            onClick={() => void handleConfirm()}
            className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-900 disabled:opacity-50"
            data-testid="ignore-alert-confirm"
          >
            {submitting ? "Saving…" : "Add rule & hide alert"}
          </button>
        </div>
      </div>
    </div>
  );
}
